<template>
  <div class="modal fade" id="modal_import_balanta" tabindex="-1" aria-hidden="true" ref="modalRef">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="fw-bold">Import Balanță SAGA (Preluare)</h2>
          <div class="btn btn-icon btn-sm btn-active-icon-primary" @click="hideModal">
            <i class="fa-solid fa-xmark fs-1"></i>
          </div>
        </div>

        <div class="modal-body py-10 px-lg-17">
          <!-- Step 1: Upload -->
          <div v-if="step === 'upload'">
            <div class="notice d-flex bg-light-primary rounded border-primary border border-dashed mb-9 p-6">
              <i class="fa-solid fa-circle-info fs-2tx text-primary me-4"></i>
              <div class="d-flex flex-stack flex-grow-1">
                <div class="fw-semibold">
                  <h4 class="text-gray-900 fw-bold">Instrucțiuni Import</h4>
                  <div class="fs-6 text-gray-700">
                    Încarcă fișierul <strong>.xls</strong> generat din SAGA (Balanța de verificare). 
                    Sistemul va analiza conturile și sumele pentru a genera nota de preluare automată.
                  </div>
                </div>
              </div>
            </div>

            <div class="fv-row mb-8">
              <label class="d-flex align-items-center fs-6 fw-semibold mb-2">
                <span class="required">Selectează fișierul .xls</span>
              </label>
              <input type="file" class="form-control form-control-solid" @change="onFileChange" accept=".xls,.xlsx" />
            </div>

            <div class="fv-row mb-8">
                <label class="fs-6 fw-semibold mb-2">Data Preluării (Prima zi de activitate)</label>
                <input type="date" class="form-control form-control-solid" v-model="data_preluare" />
            </div>
          </div>

          <!-- Step 2: Preview & Edit -->
          <div v-if="step === 'preview'">
            <div class="alert align-items-center p-5 mb-10" :class="totals.balanced ? 'alert-light-success' : 'alert-light-danger'">
              <div class="d-flex flex-column">
                <h4 class="mb-1 text-dark" v-if="totals.balanced">
                   <i class="fa-solid fa-check-circle text-success me-2"></i> Balanța este echilibrată!
                </h4>
                <h4 class="mb-1 text-dark" v-else>
                   <i class="fa-solid fa-triangle-exclamation text-danger me-2"></i> Balanța NU este echilibrată! (Diferență: {{ totals.diff.toFixed(2) }})
                </h4>
                <span>Total Debit: <strong>{{ formatVal(totals.debit) }}</strong> | Total Credit: <strong>{{ formatVal(totals.credit) }}</strong></span>
              </div>
            </div>

            <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
              <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr class="fw-bold text-muted bg-light">
                    <th class="ps-4 min-w-80px">Cont</th>
                    <th class="min-w-200px">Denumire SAGA / Sugestie</th>
                    <th class="min-w-100px text-end">Debit</th>
                    <th class="min-w-100px text-end">Credit</th>
                    <th class="min-w-100px text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in previewData.items" :key="idx">
                    <td class="ps-4 fw-bold">{{ item.cont }}</td>
                    <td>
                      <input v-if="!item.exists" type="text" class="form-control form-control-sm border-warning" v-model="item.suggestion" />
                      <span v-else class="text-gray-800">{{ item.suggestion }}</span>
                    </td>
                    <td class="text-end">
                      <input type="number" step="0.01" class="form-control form-control-sm text-end input-sum" v-model.number="item.debit" />
                    </td>
                    <td class="text-end text-end">
                      <input type="number" step="0.01" class="form-control form-control-sm text-end input-sum" v-model.number="item.credit" />
                    </td>
                    <td class="text-center">
                      <span v-if="item.exists" class="badge badge-light-success">Existent</span>
                      <span v-else class="badge badge-light-warning" title="Acest cont va fi creat automat">Nou</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer flex-center">
          <button type="button" class="btn btn-light me-3" @click="step === 'preview' ? step = 'upload' : hideModal()">
            {{ step === 'preview' ? 'Inapoi' : 'Renunță' }}
          </button>
          
          <button v-if="step === 'upload'" type="button" class="btn btn-primary" @click="handlePreview" :disabled="!selectedFile || loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            Pasul următor (Preview)
          </button>

          <button v-if="step === 'preview'" type="button" class="btn btn-success" @click="handleSave" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            Confirmă și Încarcă Balanța
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';

export default {
  name: 'ModalImportBalanta',
  props: {
    firmaId: { type: Number, required: true }
  },
  data() {
    return {
      step: 'upload',
      loading: false,
      selectedFile: null,
      data_preluare: new Date().toISOString().split('T')[0],
      previewData: null,
      modalInstance: null
    }
  },
  mounted() {
    this.modalInstance = new Modal(this.$refs.modalRef);
  },
  computed: {
    totals() {
        if (!this.previewData || !this.previewData.items) return { debit: 0, credit: 0, balanced: true, diff: 0 };
        
        let d = 0, c = 0;
        this.previewData.items.forEach(i => {
            d += Number(i.debit || 0);
            c += Number(i.credit || 0);
        });
        
        const diff = d - c;
        return {
            debit: d,
            credit: c,
            balanced: Math.abs(diff) < 0.01,
            diff: diff
        };
    }
  },
  methods: {
    showModal() {
      this.step = 'upload';
      this.selectedFile = null;
      this.previewData = null;
      this.modalInstance.show();
    },
    hideModal() {
      this.modalInstance.hide();
    },
    onFileChange(e) {
      this.selectedFile = e.target.files[0];
    },
    async handlePreview() {
      if (!this.selectedFile) return;
      this.loading = true;
      
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      formData.append('id_firma', this.firmaId);

      try {
        const res = await axios.post('/api/contabilitate/balanta/import-preview', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.previewData = res.data;
        this.step = 'preview';
      } catch (e) {
        const msg = e.response?.data?.error || e.message || "Eroare la procesarea fișierului.";
        Swal.fire({
          title: "Eroare",
          html: `<div style="text-align: left; max-height: 300px; overflow-y: auto; font-size: 0.9rem; font-family: monospace;">${msg}</div>`,
          icon: "error",
          footer: '<button id="copy-error-preview" class="btn btn-sm btn-light-primary"><i class="fa fa-copy me-1"></i> Copiază textul erorii</button>',
          didOpen: () => {
            const btn = document.getElementById('copy-error-preview');
            if (btn) {
              btn.onclick = () => {
                navigator.clipboard.writeText(msg);
                btn.innerHTML = '<i class="fa fa-check me-1"></i> Copiat!';
              }
            }
          }
        });
      } finally {
        this.loading = false;
      }
    },
    async handleSave() {
      if (!this.totals.balanced) {
          const result = await Swal.fire({
              title: "Atenție! Balanță de-echilibrată",
              text: `Balanța are o diferență de ${this.totals.diff.toFixed(2)}. Sigur doriți să finalizați importul în aceste condiții?`,
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Da, încarcă așa",
              cancelButtonText: "Nu, anulează",
              confirmButtonColor: "#f1416c"
          });
          
          if (!result.isConfirmed) return;
      }

      this.loading = true;
      try {
        const res = await axios.post('/api/contabilitate/balanta/import-save', {
          id_firma: this.firmaId,
          items: this.previewData.items,
          data_preluare: this.data_preluare
        });
        
        const rjId = res.data.rj_id;

        Swal.fire({
          title: "Succes!",
          text: "Balanța a fost importată cu succes și nota de preluare a fost generată.",
          icon: "success",
          showCancelButton: true,
          confirmButtonText: '<i class="fa fa-file-pdf me-1"></i> Descarcă Nota de Preluare',
          cancelButtonText: 'Închide',
          confirmButtonColor: "#f1416c"
        }).then((result) => {
          if (result.isConfirmed) {
             window.open(`/api/contabilitate/rj/pdf/${rjId}`, '_blank');
          }
        });

        this.hideModal();
        this.$emit('imported');
      } catch (e) {
        const msg = e.response?.data?.error || e.message || "Eroare la salvarea datelor.";
        Swal.fire({
          title: "Eroare",
          html: `<div style="text-align: left; max-height: 300px; overflow-y: auto; font-size: 0.9rem; font-family: monospace;">${msg}</div>`,
          icon: "error",
          footer: '<button id="copy-error-save" class="btn btn-sm btn-light-primary"><i class="fa fa-copy me-1"></i> Copiază textul erorii</button>',
          didOpen: () => {
            const btn = document.getElementById('copy-error-save');
            if (btn) {
              btn.onclick = () => {
                navigator.clipboard.writeText(msg);
                btn.innerHTML = '<i class="fa fa-check me-1"></i> Copiat!';
              }
            }
          }
        });
      } finally {
        this.loading = false;
      }
    },
    formatVal(val) {
      if (!val || val === 0) return '-';
      return Number(val).toLocaleString('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
}
</script>

<style scoped>
.modal-xl {
  max-width: 1140px;
}
.border-warning {
  border-color: #ffc107 !important;
  background-color: #fff9e6;
}
.input-sum {
    border: 1px solid transparent;
    background: transparent;
    padding: 0.2rem;
    font-weight: bold;
    max-width: 120px;
    margin-left: auto;
}
.input-sum:hover, .input-sum:focus {
    border-color: #e4e6ef !important;
    background: #fff !important;
}
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
